/**
 * Date range + compare controls, kept in the URL (?range=7d&compare=0, or
 * ?from=…&to=…) so a link to "last 30 days" opens on last 30 days. Writes
 * only on a choice, never per keystroke.
 */
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CalendarRange } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import {
  RANGE_PRESETS,
  formatRangeLabel,
  isIsoDay,
  localDate,
  presetRange,
  type DateRange,
  type RangePreset,
} from '@/lib/yom/range';

export interface RangeState {
  preset: RangePreset | 'custom';
  range: DateRange;
  compare: boolean;
}

const PRESET_VALUES = RANGE_PRESETS.map((p) => p.value) as string[];

export function useRangeState(defaultPreset: RangePreset = '7d') {
  const [params, setParams] = useSearchParams();
  const state = useMemo<RangeState>(() => {
    const from = params.get('from');
    const to = params.get('to');
    const compare = params.get('compare') !== '0';
    if (isIsoDay(from) && isIsoDay(to) && from <= to) {
      return { preset: 'custom', range: { from, to }, compare };
    }
    const raw = params.get('range');
    const preset = (raw && PRESET_VALUES.includes(raw) ? raw : defaultPreset) as RangePreset;
    return { preset, range: presetRange(preset), compare };
  }, [params, defaultPreset]);

  const update = (next: Partial<{ preset: RangePreset; custom: DateRange; compare: boolean }>) => {
    const p = new URLSearchParams(params);
    if (next.preset) {
      p.delete('from');
      p.delete('to');
      if (next.preset === defaultPreset) p.delete('range');
      else p.set('range', next.preset);
    }
    if (next.custom) {
      p.delete('range');
      p.set('from', next.custom.from);
      p.set('to', next.custom.to);
    }
    if (next.compare !== undefined) {
      if (next.compare) p.delete('compare');
      else p.set('compare', '0');
    }
    setParams(p, { replace: true });
  };
  return { state, update };
}

export function RangeControls({
  state,
  update,
  showCompare = true,
}: {
  state: RangeState;
  update: ReturnType<typeof useRangeState>['update'];
  showCompare?: boolean;
}) {
  const [customOpen, setCustomOpen] = useState(state.preset === 'custom');
  const [draft, setDraft] = useState<DateRange>(state.range);
  const today = localDate(new Date());

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
      <div
        role="radiogroup"
        aria-label="Date range"
        className="bg-muted/60 grid grid-cols-5 rounded-lg p-1 sm:inline-flex"
      >
        {RANGE_PRESETS.map((p) => (
          <button
            key={p.value}
            type="button"
            role="radio"
            aria-checked={state.preset === p.value}
            onClick={() => {
              setCustomOpen(false);
              update({ preset: p.value });
            }}
            className={cn(
              'rounded-md px-2 py-1.5 text-xs font-medium transition-colors sm:px-3 sm:text-sm',
              state.preset === p.value
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {p.short}
          </button>
        ))}
        <button
          type="button"
          role="radio"
          aria-checked={state.preset === 'custom'}
          onClick={() => {
            setDraft(state.range);
            setCustomOpen((o) => !o);
          }}
          className={cn(
            'inline-flex items-center justify-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors sm:px-3 sm:text-sm',
            state.preset === 'custom'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          <CalendarRange className="h-3.5 w-3.5" aria-hidden />
          <span className="hidden sm:inline">
            {state.preset === 'custom' ? formatRangeLabel(state.range) : 'Custom'}
          </span>
          <span className="sm:hidden">Dates</span>
        </button>
      </div>

      {customOpen && (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (isIsoDay(draft.from) && isIsoDay(draft.to) && draft.from <= draft.to) {
              update({ custom: draft });
            }
          }}
        >
          <label className="text-muted-foreground flex flex-col text-xs">
            From
            <input
              type="date"
              value={draft.from}
              max={draft.to || today}
              onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
              className="bg-background text-foreground mt-1 h-9 rounded-md border px-2 text-sm"
            />
          </label>
          <label className="text-muted-foreground flex flex-col text-xs">
            To
            <input
              type="date"
              value={draft.to}
              min={draft.from}
              max={today}
              onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
              className="bg-background text-foreground mt-1 h-9 rounded-md border px-2 text-sm"
            />
          </label>
          <button
            type="submit"
            className="bg-primary text-primary-foreground hover:bg-primary/90 h-9 rounded-md px-3 text-sm font-medium"
          >
            Apply
          </button>
        </form>
      )}

      {showCompare && (
        <label className="text-muted-foreground inline-flex cursor-pointer items-center gap-2 text-sm sm:ml-auto">
          <Switch
            checked={state.compare}
            onCheckedChange={(v) => update({ compare: v })}
            aria-label="Compare with the period before"
          />
          Compare with the period before
        </label>
      )}
    </div>
  );
}
